import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "src/environments/environment";
import {
  IDEA_ATTACHMENT_DIR,
  KAIZEN_ATTACHMENT_DIR,
} from "../components/idea-files/idea-files.constants";
import { DeleteIdeaFileGQL } from "src/generated/graphql/idea/idea-file/delete-file.graphql";

export type IdeaAttachmentFlow = "idea" | "kaizen";

export interface IdeaAttachmentResult {
  name: string;
  ok: boolean;
  message?: string;
}

/**
 * Anexos de ideia (tradicional e Kaizen) no detalhe do admin — espelho do
 * serviço do euro-click-web. Upload REST multipart (um arquivo por vez, sem
 * abortar os demais se um falhar) e remoção (soft-delete) por fluxo.
 * As mensagens de erro são as da API (pt); só 413 (multer) é traduzido aqui.
 */
@Injectable({
  providedIn: "root",
})
export class IdeaAttachmentService {
  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private deleteIdeaFileGQL: DeleteIdeaFileGQL,
  ) {}

  async uploadAll(
    flow: IdeaAttachmentFlow,
    ideaId: string,
    files: File[],
    dir?: string,
  ): Promise<IdeaAttachmentResult[]> {
    const folder = dir || (flow === "idea" ? IDEA_ATTACHMENT_DIR : KAIZEN_ATTACHMENT_DIR);
    const url =
      flow === "idea"
        ? `${environment.base_url}/ideas/file/${ideaId}/${folder}`
        : `${environment.base_url}/idea-kaizen-file/${ideaId}/${folder}`;

    const results: IdeaAttachmentResult[] = [];
    for (const file of files || []) {
      const formData = new FormData();
      formData.append("file", file, file.name);
      try {
        await this.http.post(url, formData).toPromise();
        results.push({ name: file.name, ok: true });
      } catch (err) {
        results.push({ name: file.name, ok: false, message: this.errorMessage(err, "attachments.errorGeneric") });
      }
    }
    return results;
  }

  /** Remove (soft-delete) um anexo da ideia tradicional (GraphQL `deleteIdeaFile`). */
  async removeIdeaFile(fileId: string): Promise<IdeaAttachmentResult> {
    try {
      await this.deleteIdeaFileGQL.mutate({ id: fileId }).toPromise();
      return { name: fileId, ok: true };
    } catch (err) {
      return { name: fileId, ok: false, message: this.errorMessage(err, "attachments.removeError") };
    }
  }

  /** Remove (soft-delete) um anexo do Kaizen (REST `DELETE /idea-kaizen-file/:id`). */
  async removeKaizenFile(fileId: string): Promise<IdeaAttachmentResult> {
    try {
      await this.http.delete(`${environment.base_url}/idea-kaizen-file/${fileId}`).toPromise();
      return { name: fileId, ok: true };
    } catch (err) {
      return { name: fileId, ok: false, message: this.errorMessage(err, "attachments.removeError") };
    }
  }

  /** Resumo do envio; `null` quando não houve arquivos. */
  summarize(results: IdeaAttachmentResult[]): { type: "success" | "warning"; html: string } | null {
    if (!results?.length) {
      return null;
    }
    const sent = results.filter((r) => r.ok).length;
    const failed = results.filter((r) => !r.ok);
    let html = this.translate.instant("attachments.summarySent", { count: sent, total: results.length });
    if (failed.length) {
      html +=
        "<br><br>" +
        this.translate.instant("attachments.summaryFailed") +
        "<br>" +
        failed.map((r) => `<b>${this.escape(r.name)}</b>: ${this.escape(r.message)}`).join("<br>");
    }
    return { type: failed.length ? "warning" : "success", html };
  }

  /**
   * Mensagem da API em GraphQL ou REST (pt). 413 vem do multer, em inglês.
   * Sem mensagem utilizável, cai no texto padrão (`fallbackKey`).
   */
  errorMessage(err: any, fallbackKey: string): string {
    if ((err as HttpErrorResponse)?.status === 413) {
      return this.translate.instant("attachments.errorSize");
    }
    const graphQLMessage = err?.graphQLErrors?.[0]?.message;
    if (graphQLMessage) {
      return graphQLMessage;
    }
    const message = err?.error?.message;
    if (Array.isArray(message) && message.length) {
      return message.join(", ");
    }
    if (typeof message === "string" && message) {
      return message;
    }
    return this.translate.instant(fallbackKey);
  }

  private escape(text: string): string {
    return (text || "").replace(
      /[&<>"']/g,
      (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]),
    );
  }
}
